/**
 * Product identity, injected once by each app at boot.
 *
 * This is the injection point that makes **"a build never branches on which app
 * it is"** possible. There is no `if (flavor === 'admin')` anywhere in this
 * package: where the two products genuinely differ, the difference arrives here
 * as data, and the shared code reads a value rather than testing an identity.
 *
 * The moment a shared component needs to know *which* app it is in order to
 * behave correctly, the right move is to add a field here — not a condition
 * there. A condition is invisible from the call site and multiplies; a field is
 * declared by the app that needs it.
 */

/** Which application this build is. `partners` is the Partner Portal
 *  (`docs/30_PARTNER_PORTAL.md`), served on its own host. */
export type AppFlavor = 'admin' | 'app' | 'partners';

/**
 * Which principal kind the build serves. A build serves exactly one and refuses
 * the other at sign-in — a client never downloads the bundle containing staff
 * screens, and an employee mailed the wrong URL gets a door they cannot open
 * rather than a half-working app.
 *
 * The refusal here is UX. The server re-decides it independently on every call.
 */
export type ServedKind = 'employee' | 'client_user' | 'partner_user';

export interface AppConfig {
  /** `admin`, `app` or `partners`. Sent as `X-Connect-App` (which door to
   *  sign in at) and used for storage-key namespacing; never for behaviour. */
  flavor: AppFlavor;
  /** What the product calls itself in the chrome and the document title. */
  appName: string;
  /** The principal kind this build serves. */
  servedKind: ServedKind;
  /**
   * Where the API is.
   *
   * Resolved in this order, and never hardcoded into a build:
   *   1. this value, if an app sets one explicitly;
   *   2. `window.__CONNECT_CONFIG__.apiBaseUrl`, injected at HTML render time
   *      by the deployment;
   *   3. `/api` — relative, so nginx routes it.
   *
   * The third is the normal case and the reason Connect can be deployed behind
   * any ingress, at any domain, without rebuilding. A self-hosted product whose
   * bundle contains its customer's hostname has to be rebuilt per customer.
   */
  apiBaseUrl?: string;
  /** The product version (`0.1.0`), shown in the footer. Never a commit hash:
   *  the people reading it cannot use one. */
  version?: string;
  /**
   * The app's own icon, as a URL the app serves, drawn wherever the product's
   * mark goes — the sign-in page and the shell header. Absent, the mark is the
   * product's initial on the accent tile, so an app without artwork still has one.
   */
  logoSrc?: string;
  /**
   * Whether registers show the save / auto-save control (`control`) or keep
   * every arrangement without offering one (`always`, the default).
   *
   * Staff work dozens of registers and curate their layouts, so the admin
   * portal asks for the control; a client has a handful of screens, and for
   * them the switch offers only a way to break something quietly. Declared by
   * the app that wants it — the shared table reads this value and never asks
   * which app it is in. Either way, an arrangement is saved.
   */
  layoutSaving?: 'control' | 'always';
}

declare global {
  interface Window {
    __CONNECT_CONFIG__?: { apiBaseUrl?: string };
  }
}

let current: AppConfig | null = null;

/** Called once, from the app's entry point, before anything renders. */
export function configureApp(config: AppConfig): void {
  current = config;
}

/**
 * The configured identity.
 *
 * Throws rather than returning a default. A default would let a component
 * render with the wrong product name or the wrong served kind and look like it
 * worked, which is exactly the failure this whole mechanism exists to prevent.
 */
export function appConfig(): AppConfig {
  if (current === null) {
    throw new Error(
      'configureApp() has not been called. Call it from the app entry point ' +
        'before rendering — see the admin app main.tsx.',
    );
  }
  return current;
}

/** The principal kind this build serves. */
export function servedKind(): ServedKind {
  return appConfig().servedKind;
}

/** Whether the config has been installed. For a boot-order assertion only. */
export function isConfigured(): boolean {
  return current !== null;
}

/** The resolved API base URL. See {@link AppConfig.apiBaseUrl}. */
export function apiBaseUrl(): string {
  const configured = current?.apiBaseUrl;
  if (configured) return configured.replace(/\/$/, '');
  const injected = typeof window !== 'undefined' ? window.__CONNECT_CONFIG__?.apiBaseUrl : undefined;
  if (injected) return injected.replace(/\/$/, '');
  return '/api';
}

/**
 * A storage key namespaced to this build.
 *
 * Namespaced because the two apps can be served from the same origin behind one
 * nginx, and an un-namespaced key means the client app and the admin portal
 * share a saved table layout — which reads as a corrupted layout in whichever
 * one is opened second.
 */
export function storageKey(suffix: string): string {
  return `connect.${current?.flavor ?? 'app'}.${suffix}`;
}

/** Reset. Exists for the tests; nothing in the product calls it. */
export function resetAppConfig(): void {
  current = null;
}
